import React, { useState, useEffect } from 'react';
import InputField from '@/components/atoms/InputField';
import SliderField from '@/components/atoms/SliderField';
import { UseFormRegister } from 'react-hook-form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SliderInputFieldProps {
  name: string;
  label?: string;
  description?: string;
  value?: number | '';
  onChange?: (value: number | '') => void;
  min?: number;
  maxValue: number; // required
  step?: number;
  error?: any;
  disabled?: boolean;
  icon?: string;
  iconAlt?: string;
  inputPlaceholder?: string;
  className?: string;
  register?: UseFormRegister<any>;
  tooltipText?: string;
  onValueExceed?: () => void;
}

const SliderInputField: React.FC<SliderInputFieldProps> = ({
  name,
  label,
  description,
  value,
  onChange,
  min = 0,
  maxValue,
  step = 1,
  error,
  disabled = false,
  icon,
  iconAlt,
  inputPlaceholder,
  className,
  register,
  tooltipText,
  onValueExceed
}) => {
  const isEmpty = value === '' || value === undefined || value === null;
  const numericValue = isEmpty ? 0 : Number(value);

  // Calculate percent from value
  const getPercent = (val: number) => {
    if (!maxValue) return 0;
    return Math.round(((val - min) / (maxValue - min)) * 100);
  };
  // Calculate value from percent
  const getValue = (percent: number) => {
    return Math.round((percent / 100) * (maxValue - min) + min);
  };

  const [internalValue, setInternalValue] = useState<number | ''>(isEmpty ? '' : numericValue);
  const [percent, setPercent] = useState<number>(getPercent(numericValue));

  // Keep internal value in sync with prop
  useEffect(() => {
    setInternalValue(isEmpty ? '' : numericValue);
    setPercent(getPercent(numericValue));
    // eslint-disable-next-line
  }, [value, maxValue, min]);

  // Handle slider change (percent)
  const handleSliderChange = (val: number[]) => {
    const newPercent = val[0];
    const newValue = getValue(newPercent);
    setPercent(newPercent);
    setInternalValue(newValue);
    if (onChange) onChange(newValue);
  };

  // Handle input change (actual value)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d.]/g, '');
    if (raw === '') {
      setInternalValue('');
      setPercent(0);
      if (onChange) onChange('');
      return;
    }
    let num = Number(raw);
    if (isNaN(num)) num = min;

    if (num > maxValue) {
      if (onValueExceed) {
        onValueExceed();
      }
      num = maxValue;
    }

    if (num < min) num = min;
    setInternalValue(num);
    setPercent(getPercent(num));
    if (onChange) onChange(num);
  };

  return (
    <div className={className || ''}>
      {label && (
        <div className="flex items-center gap-2">
          <label className="text-[#495057] font-medium text-xs md:text-sm mb-1 block">{label}</label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="text-primary">
                  ?
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      <SliderField
        name={name + '-slider'}
        value={[percent]}
        onValueChange={handleSliderChange}
        min={0}
        max={100}
        step={1}
        unit="%"
        disabled={disabled}
        // error={error}
      />
      <InputField
        name={name}
        type="number"
        value={internalValue === '' ? '' : internalValue.toString()}
        onChange={handleInputChange}
        placeholder={inputPlaceholder}
        icon={icon}
        iconAlt={iconAlt}
        disabled={disabled}
        error={error}
        className={`mt-1 ${error ? 'border-red-500' : ''}`}
        register={register}
      />
    </div>
  );
};

export default SliderInputField;
